import React, { useState, useEffect, useRef } from 'react';
import { css } from '@emotion/css';
import SelectField from './SelectField';
import TextField from './TextField';
import { color_shades_dark, color_colors_ocean, color_text_light, color_variants_ocean_light_1 } from '../../constants/colors';
import { convert12HourTo24Hour, convert24HourTo12Hour } from '../../utils/weeklyScheduleFormatters';
import { formatDateForBackend, parseDateFromBackend, TimeRange, SpecialDate } from '../../utils/scheduleFormatters';

const addIcon = require('../../content/images/add-circle.svg');
const deleteIcon = require('../../content/images/trash.svg');

interface SpecialDateFieldProps {
    name?: string;
    label?: string;
    value?: string; // JSON string of SpecialDate[]
    onChange?: (value: string) => void;
    onBlur?: () => void;
    disabled?: boolean;
    className?: string;
    onValidationChange?: (hasErrors: boolean) => void;
    defaultValue?: string;
    onReset?: () => void;
}

// Modern select styles
const modernSelectStyle = css`
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    width: 100%;
    min-width: 140px;
    .Select-control {
        border: 1px solid ${color_shades_dark};
        border-radius: 6px;
        min-height: 44px;
        background: white;
        transition: all 0.2s ease;
        
        &:hover {
            border-color: ${color_colors_ocean};
        }
    }
    
    &.is-focused:not(.is-open) .Select-control {
        border-color: ${color_colors_ocean};
        box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
    }
    
    .Select-placeholder,
    .Select-value {
        padding: 11px 14px;
        font-size: 14px;
        line-height: 1.4;
        display: flex;
        align-items: center;
    }
    
    .Select-value-label {
        color: ${color_text_light};
    }
    
    .Select-input {
        padding: 11px 14px;
        height: 44px;
        align-items: center;
    }
    
    .Select-input > input {
        padding: 0 0 0 2px;
        line-height: 1.4;
        font-size: 14px;
        color: ${color_text_light};
    }
    
    .Select-menu-outer {
        border: 1px solid ${color_shades_dark};
        border-radius: 6px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        margin-top: 4px;
        z-index: 1000;
    }
    
    .Select-menu {
        max-height: 240px;
        border-radius: 6px;
    }
    
    .Select-option {
        padding: 10px 12px;
        font-size: 14px;
        cursor: pointer;
        transition: background 0.15s ease;
        
        &.is-focused {
            background: #f0f4f8;
            color: ${color_text_light};
        }
        
        &.is-selected {
            background: ${color_colors_ocean};
            color: white;
        }
    }
    
    .Select-arrow-zone {
        padding-right: 8px;
    }
    
    .Select-arrow {
        border-color: ${color_text_light} transparent transparent;
        border-width: 5px 4px 2.5px;
    }
    
    &.is-open .Select-arrow {
        border-color: transparent transparent ${color_text_light};
        border-width: 0 4px 5px;
    }
`;

const SpecialDateField: React.FC<SpecialDateFieldProps> = (props) => {
    // Parse the value from backend format: MM/DD/YYYY or MM/DD/YYYY(HH:MM-HH:MM|HH:MM-HH:MM) or 'none'
    const parseValue = (val: string | undefined): { dates: SpecialDate[], hasNoSpecialDates: boolean } => {
        if (!val || val === 'none') {
            return { dates: [], hasNoSpecialDates: val === 'none' };
        }

        // Try to parse as backend format first
        try {
            const dates: SpecialDate[] = [];
            const dateEntries = val.split(',').filter(entry => entry.trim());
            let hasInvalidDate = false;

            for (const entry of dateEntries) {
                const match = entry.match(/^([^(]+)\(([^)]+)\)$/);
                if (match) {
                    // Date with time ranges: MM/DD/YYYY(HH:MM-HH:MM)
                    // Backend stores CLOSED hours, we need to convert to OPEN hours for the UI
                    const dateStr = match[1].trim();
                    const timesStr = match[2].trim();
                    const formattedDate = parseDateFromBackend(dateStr);

                    // Check if date parsing failed
                    if (formattedDate === 'none') {
                        hasInvalidDate = true;
                        continue;
                    }

                    // Parse closed periods from backend
                    const closedPeriods: Array<{ start: string; end: string }> = [];
                    const timeRangePairs = timesStr.split('|');
                    for (const pair of timeRangePairs) {
                        const [start, end] = pair.split('-');
                        if (start && end) {
                            closedPeriods.push({
                                start: start.trim(),
                                end: end.trim()
                            });
                        }
                    }

                    // Convert closed periods to open periods (inverse)
                    const openTimeRanges: TimeRange[] = [];
                    const sortedClosedPeriods = closedPeriods.sort((a, b) => a.start.localeCompare(b.start));

                    // If first closed period doesn't start at 00:00, there's an open period before it
                    if (sortedClosedPeriods.length === 0) {
                        // No closed periods means open 24 hours
                        openTimeRanges.push({
                            id: `time_${crypto.randomUUID()}`,
                            startTime: '12:00 AM',
                            endTime: '11:59 PM'
                        });
                    } else {
                        if (sortedClosedPeriods[0].start !== '00:00') {
                            openTimeRanges.push({
                                id: `time_${crypto.randomUUID()}`,
                                startTime: '12:00 AM',
                                endTime: convert24HourTo12Hour(sortedClosedPeriods[0].start)
                            });
                        }

                        // Open periods are between closed periods
                        for (let i = 0; i < sortedClosedPeriods.length - 1; i++) {
                            const currentEnd = sortedClosedPeriods[i].end;
                            const nextStart = sortedClosedPeriods[i + 1].start;
                            if (currentEnd !== nextStart) {
                                openTimeRanges.push({
                                    id: `time_${crypto.randomUUID()}`,
                                    startTime: convert24HourTo12Hour(currentEnd),
                                    endTime: convert24HourTo12Hour(nextStart)
                                });
                            }
                        }

                        // If last closed period doesn't end at 23:59, there's an open period after it
                        const lastPeriod = sortedClosedPeriods[sortedClosedPeriods.length - 1];
                        if (lastPeriod.end !== '23:59') {
                            openTimeRanges.push({
                                id: `time_${crypto.randomUUID()}`,
                                startTime: convert24HourTo12Hour(lastPeriod.end),
                                endTime: '11:59 PM'
                            });
                        }
                    }

                    dates.push({
                        id: `date_${crypto.randomUUID()}`,
                        date: formattedDate,
                        isClosedAllDay: false,
                        timeRanges: openTimeRanges
                    });
                } else {
                    // Date without parentheses: MM/DD/YYYY = closed all day
                    const dateStr = entry.trim();
                    const formattedDate = parseDateFromBackend(dateStr);

                    // Check if date parsing failed
                    if (formattedDate === 'none') {
                        hasInvalidDate = true;
                        continue;
                    }

                    dates.push({
                        id: `date_${crypto.randomUUID()}`,
                        date: formattedDate,
                        isClosedAllDay: true,
                        timeRanges: []
                    });
                }
            }

            // If we encountered any invalid dates, treat as "no special dates"
            if (hasInvalidDate && dates.length === 0) {
                return { dates: [], hasNoSpecialDates: true };
            }

            if (dates.length > 0) {
                return { dates, hasNoSpecialDates: false };
            }
        } catch (e) {
            console.error('Error parsing backend format:', e);
        }

        // Fallback: try to parse as JSON (legacy format)
        try {
            const parsed = JSON.parse(val);
            if (Array.isArray(parsed) && parsed.length === 1 && parsed[0].date === 'none') {
                return { dates: [], hasNoSpecialDates: true };
            }
            return { dates: parsed, hasNoSpecialDates: false };
        } catch {
            return { dates: [], hasNoSpecialDates: false };
        }
    };

    const initialValue = parseValue(props.value);
    const [specialDates, setSpecialDates] = useState<SpecialDate[]>(initialValue.dates);
    const [hasNoSpecialDates, setHasNoSpecialDates] = useState<boolean>(initialValue.hasNoSpecialDates);

    // Ref to prevent infinite loops from internal updates
    const isUpdatingRef = useRef<boolean>(false);
    // Ref to track previous validation state to avoid redundant callbacks
    const prevHasErrorsRef = useRef<boolean | undefined>(undefined);

    // Sync state with external prop changes
    useEffect(() => {
        // Ignore updates originating from our own onChange
        if (isUpdatingRef.current) {
            isUpdatingRef.current = false;
            return;
        }

        const parsed = parseValue(props.value);
        setSpecialDates(parsed.dates);
        setHasNoSpecialDates(parsed.hasNoSpecialDates);
    }, [props.value]);

    // Generate time options in 1-minute increments
    const generateTimeOptions = () => {
        const times: { value: string; label: string }[] = [];
        const periods = ['AM', 'PM'];

        for (const period of periods) {
            for (let hour = 12; hour >= 1; hour--) {
                for (let minute = 0; minute < 60; minute++) {
                    const minuteStr = minute.toString().padStart(2, '0');
                    const value = `${hour}:${minuteStr} ${period}`;
                    times.push({ value, label: value });
                }
            }
        }

        return times.sort((a, b) => {
            const parseTime = (timeStr: string) => {
                const [time, period] = timeStr.split(' ');
                const [hour, minute] = time.split(':').map(Number);
                let hours24 = hour === 12 ? 0 : hour;
                if (period === 'PM' && hour !== 12) hours24 += 12;
                if (period === 'AM' && hour === 12) hours24 = 0;
                return hours24 * 60 + minute;
            };
            return parseTime(a.value) - parseTime(b.value);
        });
    };

    const timeOptions = generateTimeOptions();

    // Calculate closed hours from open hours for display
    const calculateClosedHours = (openTimeRanges: TimeRange[]): string => {
        if (openTimeRanges.length === 0) {
            return 'Closed All Day';
        }

        const closedPeriods: string[] = [];
        const sortedRanges = [...openTimeRanges].sort((a, b) => {
            const aStart = convert12HourTo24Hour(a.startTime);
            const bStart = convert12HourTo24Hour(b.startTime);
            return aStart.localeCompare(bStart);
        });

        // Check if closed from midnight to first opening
        const firstStart = convert12HourTo24Hour(sortedRanges[0].startTime);
        if (firstStart !== '00:00') {
            closedPeriods.push(`${convert24HourTo12Hour('00:00')}-${convert24HourTo12Hour(firstStart)}`);
        }

        // Check gaps between open periods
        for (let i = 0; i < sortedRanges.length - 1; i++) {
            const currentEnd = convert12HourTo24Hour(sortedRanges[i].endTime);
            const nextStart = convert12HourTo24Hour(sortedRanges[i + 1].startTime);
            if (currentEnd !== nextStart) {
                closedPeriods.push(`${convert24HourTo12Hour(currentEnd)}-${convert24HourTo12Hour(nextStart)}`);
            }
        }

        // Check if closed from last closing to end of day
        const lastEnd = convert12HourTo24Hour(sortedRanges[sortedRanges.length - 1].endTime);
        // End of day is 23:59, but if they close at anything before that, show the closed period
        if (lastEnd < '23:59') {
            closedPeriods.push(`${convert24HourTo12Hour(lastEnd)}-${convert24HourTo12Hour('23:59')}`);
        }

        return closedPeriods.length > 0 ? `Closed: ${closedPeriods.join(', ')}` : 'Open 24 Hours';
    };

    // Convert special dates to backend format: MM/DD/YYYY or MM/DD/YYYY(HH:MM-HH:MM|HH:MM-HH:MM)
    // For custom hours, we save the CLOSED/DISABLED hours (inverse of open hours)
    const convertToBackendFormat = (dates: SpecialDate[]): string => {
        return dates.map(specialDate => {
            const formattedDate = formatDateForBackend(specialDate.date);

            if (specialDate.isClosedAllDay) {
                // Closed all day - just the date without time ranges
                return formattedDate;
            } else if (specialDate.timeRanges.length > 0) {
                // Custom hours - calculate and save CLOSED hours (inverse of open hours)
                const closedPeriods: string[] = [];
                const sortedRanges = [...specialDate.timeRanges].sort((a, b) => {
                    const aStart = convert12HourTo24Hour(a.startTime);
                    const bStart = convert12HourTo24Hour(b.startTime);
                    return aStart.localeCompare(bStart);
                });

                // Check if closed from midnight to first opening
                const firstStart = convert12HourTo24Hour(sortedRanges[0].startTime);
                if (firstStart !== '00:00') {
                    closedPeriods.push(`00:00-${firstStart}`);
                }

                // Check gaps between open periods (closed periods)
                for (let i = 0; i < sortedRanges.length - 1; i++) {
                    const currentEnd = convert12HourTo24Hour(sortedRanges[i].endTime);
                    const nextStart = convert12HourTo24Hour(sortedRanges[i + 1].startTime);
                    if (currentEnd !== nextStart) {
                        closedPeriods.push(`${currentEnd}-${nextStart}`);
                    }
                }

                // Check if closed from last closing to end of day
                const lastEnd = convert12HourTo24Hour(sortedRanges[sortedRanges.length - 1].endTime);
                if (lastEnd !== '23:59') {
                    closedPeriods.push(`${lastEnd}-23:59`);
                }

                // If there are closed periods, format them
                if (closedPeriods.length > 0) {
                    return `${formattedDate}(${closedPeriods.join('|')})`;
                } else {
                    // Open 24 hours - no closed periods, treat as open all day (no special date)
                    return '';
                }
            } else {
                // No time ranges but not closed all day - treat as closed
                return formattedDate;
            }
        }).filter(entry => entry !== '').join(',');
    };

    const updateValue = (newDates: SpecialDate[], noSpecialDates: boolean = hasNoSpecialDates) => {
        setSpecialDates(newDates);
        setHasNoSpecialDates(noSpecialDates);
        if (props.onChange) {
            // Set ref before calling onChange to prevent useEffect from reacting
            isUpdatingRef.current = true;
            if (noSpecialDates) {
                // Send sentinel value
                props.onChange('none');
            } else {
                // Convert to backend format
                const backendFormat = convertToBackendFormat(newDates);
                props.onChange(backendFormat);
            }
        }
    };

    const handleToggleNoSpecialDates = (checked: boolean) => {
        if (checked) {
            updateValue([], true);
        } else {
            updateValue(specialDates, false);
        }
    };

    const handleAddDate = () => {
        const newDate: SpecialDate = {
            id: `date_${crypto.randomUUID()}`,
            date: '',
            isClosedAllDay: true,
            timeRanges: []
        };
        updateValue([...specialDates, newDate], false);
    };

    // TODO: Add US Federal Holidays feature once backend API is released
    // This will allow users to select federal holidays for the next 10 years via checkboxes

    const handleRemoveDate = (dateId: string) => {
        updateValue(specialDates.filter(d => d.id !== dateId));
    };

    const handleDateChange = (dateId: string, newDate: string) => {
        updateValue(specialDates.map(d =>
            d.id === dateId ? { ...d, date: newDate } : d
        ));
    };

    const handleToggleClosedAllDay = (dateId: string, isClosed: boolean) => {
        updateValue(specialDates.map(d =>
            d.id === dateId ? { ...d, isClosedAllDay: isClosed, timeRanges: isClosed ? [] : d.timeRanges } : d
        ));
    };

    const handleAddTimeRange = (dateId: string) => {
        const newTimeRange: TimeRange = {
            id: `time_${crypto.randomUUID()}`,
            startTime: '9:00 AM',
            endTime: '5:00 PM'
        };
        updateValue(specialDates.map(d =>
            d.id === dateId ? { ...d, timeRanges: [...d.timeRanges, newTimeRange] } : d
        ));
    };

    const handleRemoveTimeRange = (dateId: string, timeRangeId: string) => {
        updateValue(specialDates.map(d =>
            d.id === dateId ? { ...d, timeRanges: d.timeRanges.filter(tr => tr.id !== timeRangeId) } : d
        ));
    };

    const handleTimeRangeChange = (dateId: string, timeRangeId: string, field: 'startTime' | 'endTime', value: string) => {
        updateValue(specialDates.map(d =>
            d.id === dateId ? {
                ...d,
                timeRanges: d.timeRanges.map(tr =>
                    tr.id === timeRangeId ? { ...tr, [field]: value } : tr
                )
            } : d
        ));
    };

    // Validation - returns a map of dateId to error message
    const getValidationErrors = (): Map<string, string> => {
        const errors = new Map<string, string>();

        // Check for duplicate dates
        const seenDates = new Map<string, string>();
        for (const date of specialDates) {
            if (date.date) {
                if (seenDates.has(date.date)) {
                    // Mark both the original and duplicate
                    const originalId = seenDates.get(date.date)!;
                    errors.set(originalId, 'Duplicate dates are not allowed.');
                    errors.set(date.id, 'Duplicate dates are not allowed.');
                } else {
                    seenDates.set(date.date, date.id);
                }
            }
        }

        // Check for dates with custom hours but no time ranges
        for (const date of specialDates) {
            if (!date.isClosedAllDay && date.timeRanges.length === 0) {
                errors.set(date.id, 'Custom hours require at least one time range.');
                continue;
            }

            // Check for overlapping time ranges within a date
            if (!date.isClosedAllDay && date.timeRanges.length > 1) {
                const sortedRanges = [...date.timeRanges].sort((a, b) => {
                    const aStart = convert12HourTo24Hour(a.startTime);
                    const bStart = convert12HourTo24Hour(b.startTime);
                    return aStart.localeCompare(bStart);
                });

                for (let i = 0; i < sortedRanges.length - 1; i++) {
                    const currentEnd = convert12HourTo24Hour(sortedRanges[i].endTime);
                    const nextStart = convert12HourTo24Hour(sortedRanges[i + 1].startTime);

                    if (currentEnd > nextStart) {
                        errors.set(date.id, 'Time ranges cannot overlap.');
                        break;
                    }
                }
            }

            // Check that end time is after start time
            if (!errors.has(date.id)) {
                for (const range of date.timeRanges) {
                    const start = convert12HourTo24Hour(range.startTime);
                    const end = convert12HourTo24Hour(range.endTime);
                    if (start >= end) {
                        errors.set(date.id, 'End time must be after start time.');
                        break;
                    }
                }
            }
        }

        return errors;
    };

    const validationErrors = getValidationErrors();
    const hasErrors = validationErrors.size > 0;

    // Notify parent of validation state changes - only when state actually changes
    useEffect(() => {
        // Only call if the validation state has actually changed
        if (prevHasErrorsRef.current !== hasErrors) {
            prevHasErrorsRef.current = hasErrors;
            if (props.onValidationChange) {
                props.onValidationChange(hasErrors);
            }
        }
    }, [hasErrors]);

    return (
        <div className={`${containerStyle} ${props.className || ''}`}>
            <div className={fieldHeaderRow}>
                {props.label && <label className={labelStyle}>{props.label}</label>}
                {props.defaultValue !== undefined && props.value !== props.defaultValue && (
                    <button
                        className={resetButtonStyle}
                        type="button"
                        onClick={() => props.onReset ? props.onReset() : props.onChange && props.onChange(props.defaultValue)}
                    >
                        Reset to default
                    </button>
                )}
            </div>

            <div className={noSpecialDatesContainer}>
                <label className={checkboxOption}>
                    <input
                        type="checkbox"
                        checked={hasNoSpecialDates}
                        onChange={(e) => handleToggleNoSpecialDates(e.target.checked)}
                        disabled={props.disabled}
                    />
                    <span>We don't have any special closed dates</span>
                </label>
            </div>

            {!hasNoSpecialDates && (
                <>
                    {/* TODO: Uncomment once backend API supports US Federal Holidays */}
                    {/* <div className={federalHolidaysContainer}>
                        <div className={federalHolidaysHeader}>US Holidays</div>
                        <div className={holidaysCheckboxGrid}>
                            Add federal holiday checkboxes here
                        </div>
                    </div> */}

                    <div className={datesContainer}>
                        {specialDates.map((specialDate, index) => (
                            <div key={specialDate.id} className={dateCard}>
                                {validationErrors.has(specialDate.id) && (
                                    <div className={dateErrorStyle}>{validationErrors.get(specialDate.id)}</div>
                                )}
                                <div className={dateHeader}>
                                    <span className={dateLabel}>Date:</span>
                                    <div className={dateInputWrapper}>
                                        <TextField
                                            name={`${props.name}_date_${specialDate.id}`}
                                            type="date"
                                            value={specialDate.date}
                                            onChange={(e) => handleDateChange(specialDate.id, e.target.value)}
                                            onBlur={props.onBlur}
                                            disabled={props.disabled}
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        className={deleteButton}
                                        onClick={() => handleRemoveDate(specialDate.id)}
                                        disabled={props.disabled}
                                    >
                                        <img src={deleteIcon} alt="Remove" />
                                    </button>
                                </div>

                                <div className={optionsContainer}>
                                    <label className={radioOption}>
                                        <input
                                            type="radio"
                                            name={`${props.name}_type_${specialDate.id}`}
                                            checked={specialDate.isClosedAllDay}
                                            onChange={() => handleToggleClosedAllDay(specialDate.id, true)}
                                            disabled={props.disabled}
                                        />
                                        <span>Closed all day</span>
                                    </label>
                                    <label className={radioOption}>
                                        <input
                                            type="radio"
                                            name={`${props.name}_type_${specialDate.id}`}
                                            checked={!specialDate.isClosedAllDay}
                                            onChange={() => handleToggleClosedAllDay(specialDate.id, false)}
                                            disabled={props.disabled}
                                        />
                                        <span>Custom hours</span>
                                    </label>
                                </div>

                                {!specialDate.isClosedAllDay && specialDate.timeRanges.length > 0 && (
                                    <div className={closedHoursInfo}>
                                        {calculateClosedHours(specialDate.timeRanges)}
                                    </div>
                                )}

                                {!specialDate.isClosedAllDay && (
                                    <div className={timeRangesContainer}>
                                        <div className={openHoursLabel}>Open Hours:</div>
                                        {specialDate.timeRanges.map((timeRange) => (
                                            <div key={timeRange.id} className={timeRangeRow}>
                                                <div className={timeRangeFields}>
                                                    <SelectField
                                                        name={`${props.name}_start_${timeRange.id}`}
                                                        placeholder="Start time"
                                                        value={timeRange.startTime}
                                                        onChange={(selected: any) =>
                                                            handleTimeRangeChange(specialDate.id, timeRange.id, 'startTime', selected?.value || '')
                                                        }
                                                        options={timeOptions}
                                                        disabled={props.disabled}
                                                        clearable={false}
                                                        useForm={false}
                                                        customClassName={modernSelectStyle}
                                                        hideLabel={true}
                                                    />
                                                    <span className={timeSeparator}>—</span>
                                                    <SelectField
                                                        name={`${props.name}_end_${timeRange.id}`}
                                                        placeholder="End time"
                                                        value={timeRange.endTime}
                                                        onChange={(selected: any) =>
                                                            handleTimeRangeChange(specialDate.id, timeRange.id, 'endTime', selected?.value || '')
                                                        }
                                                        options={timeOptions}
                                                        disabled={props.disabled}
                                                        clearable={false}
                                                        useForm={false}
                                                        customClassName={modernSelectStyle}
                                                        hideLabel={true}
                                                    />
                                                </div>
                                                <button
                                                    type="button"
                                                    className={smallDeleteButton}
                                                    onClick={() => handleRemoveTimeRange(specialDate.id, timeRange.id)}
                                                    disabled={props.disabled}
                                                >
                                                    <img src={deleteIcon} alt="Remove" />
                                                </button>
                                            </div>
                                        ))}
                                        <button
                                            type="button"
                                            className={addTimeRangeButton}
                                            onClick={() => handleAddTimeRange(specialDate.id)}
                                            disabled={props.disabled}
                                        >
                                            <img src={addIcon} alt="Add" />
                                            Add time range
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))}

                        <button
                            type="button"
                            className={addDateButton}
                            onClick={handleAddDate}
                            disabled={props.disabled}
                        >
                            <img src={addIcon} alt="Add" />
                            Add special date
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

const containerStyle = css`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const fieldHeaderRow = css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0;
`;

const resetButtonStyle = css`
    background: none;
    border: none;
    color: ${color_colors_ocean};
    font-family: Muli;
    font-size: 12px;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 4px;
    text-decoration: none;
    margin-left: auto;

    &:hover {
        background-color: ${color_variants_ocean_light_1};
    }
`;

const labelStyle = css`
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 12px;
    color: ${color_text_light};
`;

const errorStyle = css`
    font-size: 14px;
    color: #d32f2f;
    margin-bottom: 12px;
    padding: 8px 12px;
    background-color: #ffebee;
    border-radius: 4px;
    border-left: 4px solid #d32f2f;
`;

const noSpecialDatesContainer = css`
    margin-bottom: 16px;
`;

const checkboxOption = css`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    cursor: pointer;
    padding: 12px;
    border: 1px solid ${color_shades_dark};
    border-radius: 6px;
    background: white;
    transition: all 0.2s;
    
    &:hover {
        background: #f9f9f9;
        border-color: ${color_colors_ocean};
    }
    
    input[type="checkbox"] {
        cursor: pointer;
        width: 18px;
        height: 18px;
    }
    
    span {
        user-select: none;
        font-weight: 500;
    }
`;

const federalHolidaysContainer = css`
    margin-bottom: 16px;
    border: 1px solid ${color_shades_dark};
    border-radius: 8px;
    padding: 16px;
    background: white;
`;

const federalHolidaysHeader = css`
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 12px;
    color: ${color_text_light};
`;

const holidaysCheckboxGrid = css`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 8px;
`;

const holidayCheckboxOption = css`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    cursor: pointer;
    padding: 8px;
    border-radius: 4px;
    transition: background 0.2s;
    
    &:hover {
        background: #f9f9f9;
    }
    
    input[type="checkbox"] {
        cursor: pointer;
        width: 16px;
        height: 16px;
        flex-shrink: 0;
    }
    
    span {
        user-select: none;
    }
`;

const datesContainer = css`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const dateCard = css`
    border: 1px solid ${color_shades_dark};
    border-radius: 8px;
    padding: 16px;
    background: white;
`;

const dateErrorStyle = css`
    font-size: 14px;
    color: #d32f2f;
    margin-bottom: 12px;
    padding: 8px 12px;
    background-color: #ffebee;
    border-radius: 4px;
    border-left: 4px solid #d32f2f;
`;

const dateHeader = css`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const dateLabel = css`
    font-size: 14px;
    font-weight: 500;
    flex-shrink: 0;
`;

const dateInputWrapper = css`
    flex: 1;
    min-width: 0;
`;

const deleteButton = css`
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.6;
    transition: opacity 0.2s;
    
    &:hover {
        opacity: 1;
    }
    
    &:disabled {
        cursor: not-allowed;
        opacity: 0.3;
    }
    
    img {
        width: 20px;
        height: 20px;
    }
`;

const optionsContainer = css`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 16px;
`;

const radioOption = css`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    cursor: pointer;
    
    input[type="radio"] {
        cursor: pointer;
    }
    
    span {
        user-select: none;
    }
`;

const closedHoursInfo = css`
    font-size: 12px;
    color: ${color_text_light};
    padding: 8px 12px;
    background: #f8f9fa;
    border-radius: 4px;
    font-style: italic;
    margin-bottom: 12px;
`;

const timeRangesContainer = css`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    background: #f9f9f9;
    border-radius: 4px;
    border: 1px solid #e8e8e8;
`;

const openHoursLabel = css`
    font-size: 13px;
    font-weight: 600;
    color: ${color_text_light};
    margin-bottom: 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const timeRangeRow = css`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const timeRangeFields = css`
    flex: 1;
    display: flex;
    align-items: center;
    gap: 12px;
`;

const timeSeparator = css`
    font-size: 16px;
    color: ${color_text_light};
    flex-shrink: 0;
`;

const smallDeleteButton = css`
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.5;
    transition: opacity 0.2s;
    
    &:hover {
        opacity: 1;
    }
    
    &:disabled {
        cursor: not-allowed;
        opacity: 0.3;
    }
    
    img {
        width: 16px;
        height: 16px;
    }
`;

const addTimeRangeButton = css`
    background: transparent;
    border: 1px dashed ${color_shades_dark};
    border-radius: 4px;
    padding: 8px 12px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 14px;
    color: ${color_colors_ocean};
    transition: all 0.2s;
    
    &:hover {
        background: #f0f0f0;
        border-color: ${color_colors_ocean};
    }
    
    &:disabled {
        cursor: not-allowed;
        opacity: 0.5;
    }
    
    img {
        width: 16px;
        height: 16px;
    }
`;

const addDateButton = css`
    background: white;
    border: 2px dashed ${color_shades_dark};
    border-radius: 8px;
    padding: 16px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 500;
    color: ${color_colors_ocean};
    transition: all 0.2s;
    
    &:hover {
        background: #f9f9f9;
        border-color: ${color_colors_ocean};
    }
    
    &:disabled {
        cursor: not-allowed;
        opacity: 0.5;
    }
    
    img {
        width: 20px;
        height: 20px;
    }
`;

export default SpecialDateField;
